import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  List,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  CalendarToday,
  Cancel,
  ContentCopy,
  Event,
  EventAvailable,
  ExpandMore,
  Link as LinkIcon,
  Receipt,
} from "@mui/icons-material";
import dayjs from "dayjs";
import type { Club, OneOffSession } from "./types";
import { buildCompactInvoiceReference } from "./invoicePaymentUtils";
import { setOneOffSessionShare } from "./api";
import MemberListItem from "./MemberListItem";

interface OneOffSessionSectionProps {
  club: Club;
  session: OneOffSession;
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onAddParticipant: (club: Club, session: OneOffSession) => void;
  onIssueInvoices: (session: OneOffSession) => void;
  onCancel: (session: OneOffSession) => void;
  onBookingSettings: (session: OneOffSession) => void;
  onRefresh: () => Promise<void> | void;
  onError: (message: string) => void;
  containerRef?: React.Ref<HTMLDivElement>;
}

const bookingModeLabel = (mode?: string) => {
  switch (mode) {
    case "public":
      return "Public booking";
    case "private":
      return "Link only";
    default:
      return "Manual only";
  }
};

const OneOffSessionSection: React.FC<OneOffSessionSectionProps> = ({
  club,
  session,
  expanded,
  onExpandChange,
  onAddParticipant,
  onIssueInvoices,
  onCancel,
  onBookingSettings,
  onRefresh,
  onError,
  containerRef,
}) => {
  const theme = useTheme();
  const [shareLoading, setShareLoading] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);

  const isInvoiced = session.invoiced_at != null;
  const sessionDateLabel = dayjs(session.session_date).format(
    "DD MMM YYYY, HH:mm",
  );
  const participantCount = session.spaces_taken ?? session.participants?.length ?? 0;
  const capacity = session.capacity;
  const uninvoicedCount =
    session.participants?.filter((p) => p.invoice_id == null).length ?? 0;
  const hasUninvoicedParticipants = uninvoicedCount > 0;
  const bookingEnabled = session.booking_mode != null && session.booking_mode !== "off";

  const shareUrl = session.share_token
    ? `${window.location.origin}/sessions/${session.share_token}`
    : null;

  const handleShareAction = async (
    action: "enable" | "regenerate" | "disable",
  ) => {
    try {
      setShareLoading(true);
      await setOneOffSessionShare(session.id, action);
      await onRefresh();
    } catch (err) {
      console.error(err);
      onError("Failed to update the session share link.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2000);
    } catch (err) {
      console.error(err);
      onError("Failed to copy the booking link.");
    }
  };

  return (
    <Box ref={containerRef} sx={{ mt: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: theme.palette.info.light + "10",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
              pr: { xs: 0.5, sm: 2 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Event sx={{ flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {session.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sessionDateLabel}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }} flexWrap="wrap" useFlexGap>
              <Chip
                label={bookingModeLabel(session.booking_mode)}
                size="small"
                variant="outlined"
              />
              {bookingEnabled && capacity != null && (
                <Chip
                  label={`${participantCount}/${capacity} booked`}
                  size="small"
                  color={
                    session.spaces_remaining === 0 ? "warning" : "info"
                  }
                />
              )}
              {isInvoiced && (
                <Chip label="Invoiced" size="small" color="secondary" />
              )}
            </Stack>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                "& > .MuiButton-root": {
                  flex: { xs: "1 1 calc(50% - 4px)", sm: "0 0 auto" },
                  minWidth: 0,
                },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<EventAvailable />}
                size="small"
                onClick={() => onBookingSettings(session)}
              >
                Booking settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutline />}
                size="small"
                onClick={() => onAddParticipant(club, session)}
              >
                Add Participant
              </Button>
              {hasUninvoicedParticipants && (
                <Button
                  variant="outlined"
                  startIcon={<Receipt />}
                  size="small"
                  color="secondary"
                  onClick={() => onIssueInvoices(session)}
                >
                  {isInvoiced ? "Issue Invoices (new)" : "Issue Invoices"}
                </Button>
              )}
              {!isInvoiced && (
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  size="small"
                  color="warning"
                  onClick={() => onCancel(session)}
                >
                  Cancel Session
                </Button>
              )}
            </Stack>
          </Box>

          {bookingEnabled && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Member booking link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Club members can use this link to book. Fee: £
                {(session.fee ?? 0).toFixed(2)} per place.
              </Typography>
              {shareUrl ? (
                <Stack spacing={1.5}>
                  <TextField
                    value={shareUrl}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyShareLink}
                      disabled={shareLoading}
                    >
                      {copiedShareLink ? "Copied" : "Copy link"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleShareAction("regenerate")}
                      disabled={shareLoading}
                    >
                      Regenerate
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      onClick={() => handleShareAction("disable")}
                      disabled={shareLoading}
                    >
                      Stop sharing
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => handleShareAction("enable")}
                  disabled={shareLoading}
                >
                  Create share link
                </Button>
              )}
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <CalendarToday
                fontSize="small"
                sx={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Session Details
              </Typography>
            </Box>
            <Typography variant="body2">{sessionDateLabel}</Typography>
            {session.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {session.notes}
              </Typography>
            )}
          </Box>

          <List>
            {session.participants?.map((participant, index) => (
              <React.Fragment key={participant.id}>
                <MemberListItem
                  member={{
                    handler_id: participant.handler_id,
                    handler_name: participant.handler_name,
                    email_address: participant.email_address,
                  }}
                  avatarColor={theme.palette.info.main}
                  action={
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      {participant.self_booked && (
                        <Chip label="Self-booked" size="small" color="primary" variant="outlined" />
                      )}
                      {participant.invoice_id != null ? (
                        <>
                          <Chip
                            label={buildCompactInvoiceReference([
                              participant.invoice_id,
                            ])}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={participant.invoice_paid ? "Paid" : "Unpaid"}
                            size="small"
                            color={participant.invoice_paid ? "success" : "warning"}
                          />
                        </>
                      ) : null}
                    </Stack>
                  }
                />
                {index < session.participants.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {participantCount === 0 && (
              <Typography variant="body2" color="text.secondary">
                No participants yet. Enable member booking or add people manually.
              </Typography>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default OneOffSessionSection;
